import React, { useCallback, useEffect, useState } from "react";
import { useEvents } from "./context/EventsContext";
import useToggleSubscription from "./hooks/usetoggleSubscription";
import useKeyboardNavigation from "./hooks/useKeyboardNavigation";
import useMenuData from "./hooks/useMenuData";
import useEPGState from "./hooks/useEPGState";
import usePendo from "./hooks/usePendo";
import EPGContent from "./EPGContent";
import EPGHeader from "./EPGHeader";
import ConfirmDialog from "../components/ConfirmDialog/ConfirmDialog";
import TermsPopup from "./components/Terms";
import EPGSidePanel from "./EPGSidePanel";
import { EVENT_STATUS, FIRST_EVENT_KEY } from "./constants/event";
import "./epg.scss";

const App = ({ deeplinkEventIdUrl, orgShouldCheckToSignTC, showMarketingButton, showExitAppPopUp, shouldApplyComcastStyles }) => {
    const { activeLiveEvent, completeLiveEvents, fetchEvents, resetFilter, loading } = useEvents();
    // EPG state management
    const { isOpen, selectedEvent, deeplinkEventId, showTermsPopup, setShowTermsPopup, openEPG, closeEPG, clearSelection, setSelectedEvent, setFocusKeyToReturn, focusKeyToReturn } = useEPGState(resetFilter, deeplinkEventIdUrl, orgShouldCheckToSignTC);
    const { sportsData, leaguesData } = useMenuData(completeLiveEvents);
    const { handleToggleSubscription, isSubscribeLoading } = useToggleSubscription(fetchEvents);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [deeplinkEvent, setDeeplinkEvent] = useState(null);
    const showSidePanel = isOpen && selectedEvent && activeLiveEvent;

    useEffect(() => {
        if (!deeplinkEventId) {
            setDeeplinkEvent(null);
        }
        if (activeLiveEvent && completeLiveEvents.length) {
            const deeplinkIsActive = deeplinkEventId === activeLiveEvent.id;

            if (!deeplinkIsActive) {
                const eventDeeplink = completeLiveEvents.find(({ id }) => id === deeplinkEventId);
                if (eventDeeplink && !selectedEvent) {
                    setDeeplinkEvent(eventDeeplink);
                    openEPG();
                    handleCardSelect({ event: eventDeeplink, focusKey: FIRST_EVENT_KEY });
                }
            }
        }
    }, [completeLiveEvents, activeLiveEvent, deeplinkEventId]);

    useEffect(() => {
        if (selectedEvent) {
            const updatedEvent = completeLiveEvents.find(({ id }) => id === selectedEvent.id);
            setSelectedEvent(updatedEvent);
        }
    }, [completeLiveEvents]);

    // Close EPG and send Escape key to wrapper if EPG is not open (to close the app)
    const handleCloseEpg = useCallback(() => {
        if (showConfirmDialog) {
            setShowConfirmDialog(false);
            return;
        }
        if (!isOpen) {
            handleShowConfirmDialog();
        }
        closeEPG();
    }, [isOpen, closeEPG]);

    const handleOpenEpg = useCallback(() => {
        if (showConfirmDialog || loading) return;
        openEPG();
    }, [showConfirmDialog, openEPG]);

    const handleShowConfirmDialog = useCallback(() => {
        setShowConfirmDialog(showExitAppPopUp);
    }, [showExitAppPopUp]);

    // Navigation
    useKeyboardNavigation(handleOpenEpg, handleCloseEpg);

    // Initialize pendo the first time the EPG opens
    usePendo(isOpen);

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchEvents();
        }
    }, [isOpen]);

    const handleCardSelect = ({ event, focusKey }) => {
        setSelectedEvent(event);
        setFocusKeyToReturn(focusKey);
        if (event.shouldSignTC) {
            setShowTermsPopup(true);
        } else if (activeLiveEvent && event.id === activeLiveEvent.id) {
            closeEPG();
        }
    };

    const handleHeaderClose = () => {
        setSelectedEvent(null);
        setFocusKeyToReturn(null);
    };

    /**
     * Determines whether the EPG should close based on event status and navigation context
     * @param {Object} event - The selected event
     * @param {Object} activeLiveEvent - The currently active live event
     * @param {string} focusKeyToReturn - The navigation key context (e.g., 'upcoming')
     * @returns {boolean} True if EPG should close, false otherwise
     */
    const shouldCloseEPG = (event, activeLiveEvent, focusKeyToReturn) => {
        const isLiveOrComing = [EVENT_STATUS.COMING, EVENT_STATUS.LIVE].includes(event.status);
        const isDifferentFromActive = event.id !== activeLiveEvent.id;
        const isScheduledAndSubscribed = event.status === EVENT_STATUS.SCHEDULED && event.isDeviceSubscribed === true;
        const isNotFromUpcoming = !focusKeyToReturn?.includes('upcoming');

        return (isLiveOrComing && isDifferentFromActive) || (isScheduledAndSubscribed && isNotFromUpcoming);
    };

    const handlePlayNow = async (event) => {
        await handleToggleSubscription(event);

        if (shouldCloseEPG(event, activeLiveEvent, focusKeyToReturn)) {
            clearSelection();
            closeEPG();
        } else {
            clearSelection();
        }
        fetchEvents(true); // Instantly force update
    };

    const handleCloseTermsPopup = () => {
        clearSelection();
        setShowTermsPopup(false);
        if (orgShouldCheckToSignTC) {
            closeEPG();
        }
    };

    // Manage app root class
    useEffect(() => {
        const appRoot = document.getElementById('app-root');
        if (!appRoot) return;

        if (isOpen) {
            appRoot.classList.add('epg-active');
        } else {
            appRoot.classList.remove('epg-active');
        }

        return () => {
            appRoot.classList.remove('epg-active');
        };
    }, [isOpen]);

    return (
        <>
            {isOpen && (
                <div className="epgContainer">
                    <EPGHeader closeEPG={closeEPG} onBack={handleHeaderClose} showMarketingButton={showMarketingButton}/>
                    <EPGContent deeplinkEvent={deeplinkEvent} shouldApplyComcastStyles={shouldApplyComcastStyles} sportsData={sportsData} leaguesData={leaguesData} onEnterPress={handleCardSelect}/>
                </div>
            )}
            {showSidePanel && <EPGSidePanel event={selectedEvent} isLoading={isSubscribeLoading || loading} isNowPlaying={activeLiveEvent.id === selectedEvent.id} onClose={clearSelection} onPlay={handlePlayNow}/>}
            {showTermsPopup && <TermsPopup event={selectedEvent} onClose={handleCloseTermsPopup} orgShouldCheckToSignTC={orgShouldCheckToSignTC}/>}
            {showConfirmDialog && (
                <ConfirmDialog
                    title="Leaving EverPass?"
                    description="You’re heading to the home screen. Stay here to keep watching."
                    confirmTitle="Go to home screen"
                    cancelTitle="Stay on EverPass"
                    onConfirmPressed={() => {
                        setShowConfirmDialog(false);
                        window.parent.postMessage({ type: 'tvremote', key: 'Escape' }, '*');
                    }}
                    onCancelPressed={() => setShowConfirmDialog(false)}
                />
            )}
        </>
    );
};
export default App;
